import { openDB } from 'idb';

/**
 * Binary storage for imported Audio Resource files, mirroring fontDB.js's
 * design one-for-one (same `idb` library, same open/transaction/CRUD shape,
 * same "bytes as a base64 data URL" convention) — see
 * docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md.
 *
 * Deliberately a separate, standalone module for exactly the reason fontDB.js
 * gives for not generalizing imageDB.js: what would be shared between the two
 * is a handful of one-line `idb` calls, and extracting a helper for that would
 * add indirection to serve one extra caller.
 *
 * AudioStore (src/stores/audioStore.js) owns *when* to call these functions
 * and holds the resulting `storageId`; it never touches audio bytes itself
 * beyond passing them through here. Nothing in this module is WAV-specific —
 * a later audio kind (a generated tune, a tone sequence) that happens to have
 * bytes can reuse it unchanged.
 */

const DB_NAME = 'audio-store';
const AUDIO_FILE_STORE = 'audioFiles';

const getDB = () => openDB(DB_NAME, 1, {
    upgrade(db) {
        if (!db.objectStoreNames.contains(AUDIO_FILE_STORE)) {
            db.createObjectStore(AUDIO_FILE_STORE, { keyPath: 'id', autoIncrement: true });
        }
    },
});

/** Store an audio file's bytes (as a base64 data URL) + its own metadata. Returns the new row's id. */
export const addAudioFile = async (data, originalFilename, type) => {
    const db = await getDB();
    const tx = db.transaction(AUDIO_FILE_STORE, 'readwrite');
    const id = await tx.objectStore(AUDIO_FILE_STORE).add({ data, originalFilename, type });
    await tx.done;
    return id;
};

export const getAudioFile = async (id) => {
    const db = await getDB();
    const tx = db.transaction(AUDIO_FILE_STORE, 'readonly');
    const record = await tx.objectStore(AUDIO_FILE_STORE).get(id);
    await tx.done;
    return record;
};

export const deleteAudioFile = async (id) => {
    const db = await getDB();
    const tx = db.transaction(AUDIO_FILE_STORE, 'readwrite');
    await tx.objectStore(AUDIO_FILE_STORE).delete(id);
    await tx.done;
};
